import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, ChevronRight, Filter, RotateCcw, Store, TrendingUp, Users } from 'lucide-react';
import SuperAdminLayout from '../../components/superadmin/SuperAdminLayout';
import api from '../../config/axios-conf';
import { useToastContext } from '../../contexts/ToastContext';
import { formatCurrency, formatPhoneNumber } from '../../utils/formatters';
import styles from '../../assets/scss/pages/SuperAdminDashboard.module.scss';

function DashboardContent() {
    const { showError } = useToastContext();
    const navigate = useNavigate();
    const [overview, setOverview] = useState(null);
    const [recentShops, setRecentShops] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const fetchOverview = useCallback(async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);

            const [overviewRes, shopsRes] = await Promise.all([
                api.get(`/statistics/system/overview${params.toString() ? '?' + params.toString() : ''}`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                api.get('/superadmin/shops?sortBy=createdAt&order=DESC&limit=5', {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            setOverview(overviewRes.data);
            const shops = Array.isArray(shopsRes.data) ? shopsRes.data : (shopsRes.data?.data || []);
            setRecentShops(shops.slice(0, 5));
        } catch (err) {
            showError(err.response?.data?.message || 'Không thể tải dữ liệu');
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, showError]);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    const handleReset = () => {
        setStartDate('');
        setEndDate('');
    };

    const today = new Date().toLocaleDateString('vi-VN', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });

    const kpis = [
        {
            label: 'Tổng số Shops',
            value: overview?.totalShops ?? '—',
            icon: <Store />,
            iconClass: styles.kpiIconBlue,
            filter: 'all',
        },
        {
            label: 'Shops hoạt động',
            value: overview?.activeShops ?? '—',
            icon: <TrendingUp />,
            iconClass: styles.kpiIconGreen,
            filter: 'active',
        },
        {
            label: startDate && endDate ? 'Shops mới (khoảng)' : 'Shops mới hôm nay',
            value: overview?.newShops ?? '—',
            icon: <Users />,
            iconClass: styles.kpiIconOrange,
            filter: 'new',
            sub: overview?.dateRange
                ? (overview.dateRange.start === overview.dateRange.end
                    ? overview.dateRange.start
                    : `${overview.dateRange.start} – ${overview.dateRange.end}`)
                : null,
        },
        {
            label: `Doanh thu tháng ${new Date().getMonth() + 1}`,
            value: formatCurrency(overview?.totalRevenue || 0),
            icon: <BarChart3 />,
            iconClass: styles.kpiIconPurple,
            filter: 'revenue',
        },
    ];

    if (isLoading) {
        return (
            <div className={styles.loading}>
                <div className={styles.spinner} />
                Đang tải dữ liệu...
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1>Dashboard</h1>
                    <p>{today}</p>
                </div>

                <div className={styles.filter}>
                    <div className={styles.filterGroup}>
                        <label>Từ ngày</label>
                        <input
                            type="date"
                            className={styles.filterInput}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className={styles.filterGroup}>
                        <label>Đến ngày</label>
                        <input
                            type="date"
                            className={styles.filterInput}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <div className={styles.filterActions}>
                        <button className={styles.btnPrimary} onClick={fetchOverview}>
                            <Filter /> Lọc
                        </button>
                        <button className={styles.btnSecondary} onClick={handleReset}>
                            <RotateCcw /> Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className={styles.kpis}>
                {kpis.map((kpi) => (
                    <div
                        key={kpi.label}
                        className={styles.kpiCard}
                        onClick={() => navigate(`/navo-admin/shops?filter=${kpi.filter}`)}
                    >
                        <div className={styles.kpiLeft}>
                            <div className={`${styles.kpiIcon} ${kpi.iconClass}`}>
                                {kpi.icon}
                            </div>
                            <div className={styles.kpiLabel}>{kpi.label}</div>
                            <div className={styles.kpiValue}>{kpi.value}</div>
                            {kpi.sub && <div className={styles.kpiSub}>{kpi.sub}</div>}
                        </div>
                        <div className={styles.kpiArrow}>
                            <ChevronRight />
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent Shops Table */}
            <div className={styles.tableCard}>
                <div className={styles.tableHeader}>
                    <h2 className={styles.tableTitle}>Shops mới đăng ký gần đây</h2>
                    <button className={styles.tableLink} onClick={() => navigate('/navo-admin/shops')}>
                        Xem tất cả →
                    </button>
                </div>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Tên Shop</th>
                            <th>Chủ shop</th>
                            <th>SĐT</th>
                            <th>Ngày đăng ký</th>
                            <th>Tạo đơn gần nhất</th>
                            <th>Trạng thái</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recentShops.length === 0 ? (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', color: '#6B7280', padding: '32px' }}>
                                    Không có dữ liệu
                                </td>
                            </tr>
                        ) : (
                            recentShops.map((shop) => (
                                <tr key={shop.id}>
                                    <td>
                                        <div className={styles.shopName}>{shop.nameShop}</div>
                                        <div className={styles.shopOwner}>#{shop.code}</div>
                                    </td>
                                    <td>{shop.nameAdmin}</td>
                                    <td>{formatPhoneNumber(shop.phoneOne)}</td>
                                    <td>
                                        {shop.createdAt
                                            ? new Date(shop.createdAt).toLocaleDateString('vi-VN')
                                            : '—'}
                                    </td>
                                    <td>
                                        {shop.lastOrderAt
                                            ? new Date(shop.lastOrderAt).toLocaleString('vi-VN')
                                            : '—'}
                                    </td>
                                    <td>
                                        <span className={`${styles.badge} ${shop.isActive ? styles.badgeActive : styles.badgeInactive}`}>
                                            {shop.isActive ? 'Hoạt động' : 'Tạm khóa'}
                                        </span>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function Dashboard() {
    return (
        <SuperAdminLayout>
            <DashboardContent />
        </SuperAdminLayout>
    );
}
